import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { AiConversationFeatureEnum, User } from 'graphql-common';
import { GetUserAccessByCompany } from '@lib/components/UpgradePlanModal/utils/upgradePlanFeatureOptions';
import { logger } from '@lib/services/logger';
import AiAssistantModal from './AiAssistantModal';
import { AiChatDocRef } from './AiChatInsertContext';
import { AiAssistantTexts } from './getAiAssistantTexts';

export type AiAssistantLauncher = {
  isOpen: boolean;
  // Opens the assistant on a new chat; `doc` is pre-attached to the composer
  // (e.g. the document view's "Ask" button).
  open: (doc?: AiChatDocRef) => void;
};

// null when the assistant isn't available here (route, plan or user access) —
// entry points like the document view's "Ask" button hide on null.
const AiAssistantLauncherContext = createContext<AiAssistantLauncher | null>(
  null,
);

export function useAiAssistantLauncher(): AiAssistantLauncher | null {
  return useContext(AiAssistantLauncherContext);
}

type Props = {
  // False → no modal and a null launcher, so every entry point hides.
  enabled: boolean;
  user?: User | null;
  feature: AiConversationFeatureEnum;
  texts: AiAssistantTexts;
  getUserAccessByCompany?: GetUserAccessByCompany;
  children: ReactNode;
};

// Owns the one AI assistant modal of the dashboard so any page can open it —
// the floating button and in-page entry points (document "Ask") share it
// instead of each mounting their own. A provider rather than a wrapper route
// so the router tree stays flat.
export default function AiAssistantLauncherProvider({
  enabled,
  user,
  feature,
  texts,
  getUserAccessByCompany,
  children,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialDocs, setInitialDocs] = useState<AiChatDocRef[]>();

  const open = useCallback(
    (doc?: AiChatDocRef) => {
      logger.debug('AiAssistant: opened', {
        feature,
        withDocument: !!doc,
      });
      setInitialDocs(doc ? [doc] : undefined);
      setIsOpen(true);
    },
    [feature],
  );

  const isAvailable = enabled && !!user;
  // The provider outlives the modal: close it when the assistant becomes
  // unavailable (e.g. leaving the documentation section with it open), or it
  // would pop back open on return with the stale pre-attached document.
  if (!isAvailable && isOpen) {
    setIsOpen(false);
    setInitialDocs(undefined);
  }

  const launcher = useMemo(
    () => (isAvailable ? { isOpen, open } : null),
    [isAvailable, isOpen, open],
  );

  return (
    <AiAssistantLauncherContext.Provider value={launcher}>
      {children}
      {launcher && user && (
        <AiAssistantModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          user={user}
          feature={feature}
          texts={texts}
          initialDocs={initialDocs}
          getUserAccessByCompany={getUserAccessByCompany}
        />
      )}
    </AiAssistantLauncherContext.Provider>
  );
}
